import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Map, List, Clock, LogOut, Radio, Copy, Check, Route as RouteIcon, LayoutDashboard, HelpCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { copyToClipboard } from '../../lib/clipboard';
import { toast } from 'sonner';
import NotificationBell from '../../components/NotificationBell';
import { DraftsBanner } from '../../components/DraftsBanner';
import PWAInstallButton from '../../components/PWAInstallButton';
import { FULL_VERSION } from '../../version';

const SCREEN_TITLES = [
  { match: /^\/app\/stations$/, title: 'Estaciones', code: 'MO-P03' },
  { match: /^\/app\/map$/, title: 'Mapa de estaciones', code: 'MO-P02' },
  { match: /^\/app\/station\/[^/]+$/, title: 'Detalle de estación', code: 'MO-P04' },
  { match: /^\/app\/capture\/[^/]+$/, title: 'Captura de precios', code: 'MO-P05' },
  { match: /^\/app\/?(pushes)?$/, title: 'Cambios de precio', code: 'MO-P06' },
  { match: /^\/app\/history$/, title: 'Histórico de capturas', code: 'MO-P07' },
  { match: /^\/app\/route$/, title: 'Mi ruta', code: 'MO-P08' },
];

const getScreen = (pathname) => {
  for (const s of SCREEN_TITLES) if (s.match.test(pathname)) return s;
  return { title: 'PriceCapture', code: '' };
};

const ROLE_LABEL = { capturer: 'Capturador', agent: 'Agente', territory_manager: 'Territory Manager' };

const MobileLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const screen = getScreen(location.pathname);
  const [copiedCode, setCopiedCode] = useState(false);

  const handleLogout = async () => { await logout(); navigate('/'); };

  const copyCode = async () => {
    if (!screen.code) return;
    const ok = await copyToClipboard(screen.code);
    if (ok) {
      setCopiedCode(true);
      toast.success(`${screen.code} copiado`);
      setTimeout(() => setCopiedCode(false), 1400);
    } else {
      toast.error('No se pudo copiar');
    }
  };

  return (
    // App shell de altura fija (100dvh): solo <main> scrollea. Evita los bugs de
    // iOS Safari con position:fixed/sticky (la barra quedaba anclada a media
    // pantalla tras abrir el teclado o colapsar la toolbar). La barra inferior es
    // un hijo flex normal: siempre pegada abajo, sin fixed ni sticky.
    <div className="h-screen min-h-0 bg-[#f8f6f1] flex flex-col items-center" style={{ height: '100dvh' }}>
      <div className="w-full max-w-[440px] h-full min-h-0 bg-white relative flex flex-col shadow-xl">
        {/* Top header: shows the current screen name.
            pt uses env(safe-area-inset-top) so the phone status bar (iOS notch,
            Android status bar) never overlaps the title or the logout button. */}
        <header
          className="shrink-0 z-30 bg-white/95 backdrop-blur-md border-b border-[#e8e5de] px-5 pb-3 flex items-center justify-between"
          style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top, 0px))' }}
        >
          <div className="min-w-0">
            <p className="text-[10px] tracking-wider uppercase text-[#8a8579] flex items-center gap-1.5">
              {ROLE_LABEL[user?.role] || 'Usuario'}
              {screen.code && <span className="text-[#cfcabe]">·</span>}
              {screen.code && (
                <button
                  type="button"
                  onDoubleClick={copyCode}
                  title={`Doble click para copiar ${screen.code}`}
                  className="font-mono inline-flex items-center gap-1 px-1.5 py-0.5 -my-0.5 rounded text-[#8a8579] hover:bg-[#1c1c1c]/5 transition group select-none"
                  data-testid="mobile-screen-code"
                >
                  <span>{screen.code}</span>
                  {copiedCode ? <Check size={11} className="text-[#15803d]"/> : <Copy size={10} className="text-[#cfcabe] group-hover:text-[#8a8579] transition"/>}
                </button>
              )}
            </p>
            <h1 className="text-[17px] font-medium tracking-tight truncate" data-testid="mobile-screen-title">{screen.title}</h1>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <a
              href={`/manual-usuario.html#${user?.role === 'agent' ? 'agente' : user?.role === 'territory_manager' ? 'tm' : 'capturador'}`}
              target="_blank"
              rel="noreferrer"
              className="w-10 h-10 rounded-full bg-[#f3f0e9] hover:bg-[#e8e5de] flex items-center justify-center transition"
              title="Manual de uso"
              aria-label="Manual de uso"
              data-testid="mobile-help-btn"
            >
              <HelpCircle size={16} className="text-[#1c1c1c]" />
            </a>
            <PWAInstallButton />
            <NotificationBell variant="dark"/>
            {user?.role === 'territory_manager' && (
              <button
                onClick={() => navigate('/tm')}
                className="w-10 h-10 rounded-full bg-[#f3f0e9] hover:bg-[#e8e5de] flex items-center justify-center transition"
                title="Ir al panel de escritorio (TM)"
                aria-label="Panel TM"
                data-testid="mobile-tm-desktop-btn"
              >
                <LayoutDashboard size={16} className="text-[#1c1c1c]" />
              </button>
            )}
            <button
              onClick={handleLogout}
              className="w-10 h-10 rounded-full bg-[#f3f0e9] hover:bg-[#e8e5de] flex items-center justify-center transition"
              title={user?.name}
              aria-label="Cerrar sesión"
              data-testid="mobile-logout-btn"
            >
              <LogOut size={16} className="text-[#1c1c1c]" />
            </button>
          </div>
        </header>

        <main className="flex-1 min-h-0 overflow-y-auto" style={{ paddingBottom: location.pathname === '/app/map' ? 0 : '2rem' }}>
          <Outlet />
        </main>

        {/* Bottom nav: hijo flex normal del shell (sin fixed/sticky — ver nota arriba).
            pb reserva espacio para el home indicator de iOS Y para el badge
            "Made with Emergent" que vive abajo a la derecha en preview/producción. */}
        <nav
          className="shrink-0 w-full bg-white border-t border-[#e8e5de] z-[1000]"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 3.5rem)' }}
        >
          <DraftsBanner />
          <div className={`grid ${user?.role === 'capturer' ? 'grid-cols-5' : 'grid-cols-4'}`}>
            {(user?.role === 'capturer' ? [
              { to: '/app/pushes', icon: Radio, label: 'Cambios' },
              { to: '/app/route', icon: RouteIcon, label: 'Ruta' },
              { to: '/app/stations', icon: List, label: 'Estaciones' },
              { to: '/app/map', icon: Map, label: 'Mapa' },
              { to: '/app/history', icon: Clock, label: 'Histórico' },
            ] : [
              { to: '/app/pushes', icon: Radio, label: 'Cambios' },
              { to: '/app/map', icon: Map, label: 'Mapa' },
              { to: '/app/stations', icon: List, label: 'Estaciones' },
              { to: '/app/history', icon: Clock, label: 'Histórico' },
            ]).map(({ to, icon: Icon, label }) => (
              <NavLink key={to} to={to} end className="press-soft">
                {({ isActive }) => (
                  <span className={`relative flex flex-col items-center gap-1 py-3 transition-colors duration-150 ${isActive ? 'text-[#0f1d6f]' : 'text-[#8a8579]'}`}>
                    {isActive && (
                      <motion.span
                        layoutId="mobile-nav-pill"
                        transition={{ type: 'spring', bounce: 0, duration: 0.3 }}
                        className="absolute top-1.5 w-11 h-[26px] rounded-full bg-[#0f1d6f]/[0.07]"
                      />
                    )}
                    <Icon size={18} strokeWidth={isActive ? 2 : 1.6} className="relative" />
                    <span className="relative text-[10px] tracking-wide">{label}</span>
                  </span>
                )}
              </NavLink>
            ))}
          </div>
          <p className="text-center text-[9.5px] text-[#cfcabe] pb-1 tracking-wide" data-testid="mobile-app-version">{FULL_VERSION}</p>
        </nav>
      </div>
    </div>
  );
};

export default MobileLayout;
